'use client';

import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { IntentPrefetchLink } from '@/components/ui/intent-prefetch-link';
import { ShopifyImage } from '@/components/ui/shopify-image';
import type { NavItem } from './site-header.shared';

export function DesktopNavigation({ navigation }: { navigation: NavItem[] }) {
	const [openItemLabel, setOpenItemLabel] = useState<string | null>(null);
	// Mega-menu panels are `fixed` + `invisible` while closed, which still
	// counts as intersecting the viewport: every panel link (40+ on this site)
	// kicked off an App Shell prefetch and every featured image a `/_next/image`
	// fetch on each desktop page load. Panel contents mount the first time a
	// panel opens instead (and stay mounted so the close fade keeps its content).
	const [mountedPanels, setMountedPanels] = useState<ReadonlySet<string>>(() => new Set());

	const closeMenu = useCallback(() => {
		setOpenItemLabel(null);
	}, []);
	const mountPanel = useCallback((label: string) => {
		setMountedPanels((current) => (current.has(label) ? current : new Set(current).add(label)));
	}, []);
	const openItem = useCallback(
		(label: string) => {
			mountPanel(label);
			setOpenItemLabel(label);
		},
		[mountPanel],
	);

	useEffect(() => {
		if (!openItemLabel) return;
		window.addEventListener('scroll', closeMenu, { passive: true });
		return () => window.removeEventListener('scroll', closeMenu);
	}, [openItemLabel, closeMenu]);

	return (
		<ul className="hidden lg:flex lg:items-center lg:gap-0">
			{/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: desktop nav renders nested menu groups and featured links in one branch */}
			{navigation.map((item) => {
				const hasChildren = Boolean(item.children && item.children.length > 0);
				const isOpen = openItemLabel === item.label;
				const triggerStyle = item.textColor ? { color: item.textColor } : undefined;
				const triggerClassName = `relative inline-flex items-center gap-1 px-3 py-3 text-[12px] font-semibold uppercase tracking-[0.08em] transition-colors ${
					item.textColor ? '' : 'text-neutral-600 hover:text-neutral-900 focus:text-neutral-900'
				}`;

				return (
					<li
						key={item.label}
						className="static list-none"
						onDragLeave={(event) => {
							if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
								closeMenu();
							}
						}}
						onBlur={(event) => {
							if (!event.currentTarget.contains(event.relatedTarget)) {
								closeMenu();
							}
						}}
						onMouseEnter={() => {
							if (hasChildren) openItem(item.label);
						}}
						onMouseLeave={closeMenu}
					>
						{item.url && !hasChildren ? (
							<IntentPrefetchLink
								href={item.url}
								className={triggerClassName}
								style={triggerStyle}
								onClick={closeMenu}
							>
								{item.label}
							</IntentPrefetchLink>
						) : (
							<button
								type="button"
								className={triggerClassName}
								style={triggerStyle}
								aria-expanded={hasChildren ? isOpen : undefined}
								aria-haspopup={hasChildren ? 'menu' : undefined}
								onClick={(event) => {
									if (!hasChildren) return;
									// Hover already opens the menu, so a mouse click toggling it closes
									// the menu on mouseup. Only keyboard activation (detail === 0) toggles.
									if (event.detail === 0) {
										mountPanel(item.label);
										setOpenItemLabel((current) => (current === item.label ? null : item.label));
									} else {
										openItem(item.label);
									}
								}}
								onFocus={() => {
									if (hasChildren) openItem(item.label);
								}}
							>
								{item.label}
								{hasChildren && (
									<ChevronDown
										className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
									/>
								)}
								{hasChildren && (
									<span
										className={`absolute bottom-2 left-3 right-3 h-0.5 origin-left bg-neutral-200 transition-transform duration-200 ${
											isOpen ? 'scale-x-100' : 'scale-x-0'
										}`}
									/>
								)}
							</button>
						)}

						{hasChildren && item.children && item.children.length > 0 && (
							<>
								<div
									aria-hidden="true"
									className={`fixed inset-x-0 bottom-0 z-30 pointer-events-none bg-black/15 transition-opacity duration-200 ${
										isOpen ? 'opacity-100' : 'opacity-0'
									}`}
									style={{ top: 'var(--site-header-offset, 44px)' }}
								/>
								<div
									role="menu"
									className={`fixed inset-x-0 z-50 border-t border-b border-neutral-200 bg-white transition-opacity duration-200 ${
										isOpen
											? 'visible pointer-events-auto opacity-100'
											: 'invisible pointer-events-none opacity-0'
									}`}
									style={{ top: 'var(--site-header-offset, 44px)' }}
								>
									<div aria-hidden="true" className="absolute -top-3 left-0 right-0 h-3" />
									{(isOpen || mountedPanels.has(item.label)) && (
										<div className="container-page py-4">
											<div className="grid grid-cols-12 gap-4">
												<div
													className={`grid gap-6 ${item.featuredImages?.length ? 'col-span-9' : 'col-span-12'}`}
													style={{
														gridTemplateColumns: `repeat(${item.children.length}, minmax(0, 1fr))`,
													}}
												>
													{item.children.map((group) => (
														<div key={group.label}>
															<h3 className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-neutral-600">
																{group.label}
															</h3>
															{group.children && group.children.length > 0 && (
																<ul className="space-y-2">
																	{group.children.map((link) => (
																		<li key={link.label}>
																			<IntentPrefetchLink
																				href={link.url || '#'}
																				className="text-sm text-neutral-700 transition-colors hover:text-neutral-900 hover:underline underline-offset-4"
																				draggable={false}
																				onClick={closeMenu}
																			>
																				{link.label}
																			</IntentPrefetchLink>
																		</li>
																	))}
																</ul>
															)}
														</div>
													))}
												</div>

												{item.featuredImages && item.featuredImages.length > 0 && (
													<div className="col-span-3 flex flex-col gap-3">
														{item.featuredImages.map((image) => (
															<IntentPrefetchLink
																key={image.url}
																href={image.linkUrl || item.url || '#'}
																className="group/card relative block overflow-hidden rounded-sm bg-neutral-100"
																draggable={false}
																style={{
																	aspectRatio: item.featuredImages?.length === 1 ? '8/3' : '3/1',
																}}
																onClick={closeMenu}
															>
																<ShopifyImage
																	src={image.url}
																	alt={image.alt || item.label}
																	fill
																	loading="lazy"
																	className="object-cover transition-transform duration-500 group-hover/card:scale-105"
																	sizes="25vw"
																/>
																<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
																{image.label && (
																	<div className="absolute bottom-0 left-0 right-0 p-4">
																		<span className="text-xs font-bold uppercase tracking-wider text-white">
																			{image.label}
																		</span>
																	</div>
																)}
															</IntentPrefetchLink>
														))}
													</div>
												)}
											</div>

											{item.url && (
												<div className="mt-4 border-t border-neutral-200 pt-3">
													<IntentPrefetchLink
														href={item.url}
														className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-neutral-900 transition-colors hover:text-brand-accent"
														draggable={false}
														onClick={closeMenu}
													>
														Shop All {item.label}
														<span aria-hidden="true">&rarr;</span>
													</IntentPrefetchLink>
												</div>
											)}
										</div>
									)}
								</div>
							</>
						)}
					</li>
				);
			})}
		</ul>
	);
}
